import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useRestaurantContext } from '@/contexts/RestaurantContext';
import { useCurrentAssistant } from '@/hooks/useAssistantAuth';
import type { AssistantPermissions } from '@/types/entities';
import {
  useIngredients,
  useCreateIngredient,
  useUpdateIngredient,
  useDeleteIngredient,
} from '@/hooks/useIngredients';
import { useInventory, useUpdateMinThreshold } from '@/hooks/useInventory';
import { useAddReceipt } from '@/hooks/useInventoryReceipts';
import { Header } from '@/components/layout/Header';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Pencil,
  Trash2,
  Sparkles,
  XCircle,
  Lock,
  ShoppingBag,
  ClipboardCheck,
  Receipt,
  LayoutDashboard,
} from 'lucide-react';
import type { Ingredient, Inventory as InventoryType } from '@/types/entities';
import type { MeasurementUnit } from '@/types/api.types';
import { AlertModal } from '@/components/ui/alert-modal';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { InventoryEvolutionChart } from '@/components/analytics/InventoryEvolutionChart';
import { IngredientIcon } from '@/components/ui/ingredient-icon';
import { IngredientIconPicker } from '@/components/ui/ingredient-icon-picker';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CostAnalyticsDashboard } from '@/components/analytics/CostAnalyticsDashboard';
import { InventoryOverviewChart } from '@/components/analytics/InventoryOverviewChart';
import { AddReceiptDialog } from '@/components/inventory/AddReceiptDialog';
import { AddMultiIngredientReceiptDialog } from '@/components/inventory/AddMultiIngredientReceiptDialog';
import { ReceiptHistoryTable } from '@/components/inventory/ReceiptHistoryTable';
import { StockValidationDialog } from '@/components/inventory/StockValidationDialog';
import { ValidationHistoryTable } from '@/components/inventory/ValidationHistoryTable';

// Template ingredients for quick start
const TEMPLATE_INGREDIENTS = [
  {
    nameAr: 'لحم بقري',
    nameFr: 'Bœuf',
    emoji: 'cow',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 8,
    minThreshold: 3,
  },
  {
    nameAr: 'كفتة',
    nameFr: 'Viande hachée',
    emoji: 'meat-minced',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 8,
    minThreshold: 3,
  },

  // Meat & Proteins
  {
    nameAr: 'دجاج',
    nameFr: 'Poulet',
    emoji: 'chicken',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameFr: 'dinde',
    nameAr: 'ديك رومي',
    emoji: 'turkey',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,

  },
  {
    nameAr: 'جامبون',

    nameFr: 'Jambon',
    emoji: 'charcuterie',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameAr: 'صوصيص',
    nameFr: 'Sausage',
    emoji: 'sausage',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameAr: 'مأكولات بحرية',
    nameFr: 'Fruits de mer',
    emoji: 'seafood',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameFr: 'poisson',
    nameAr: 'سمك',
    emoji: 'fish',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    emoji: 'salmon',
    nameAr: 'سلمون',
    nameFr: 'Salmon',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 5,
    minThreshold: 2,
  },
  {
    nameFr: 'crevette',
    nameAr: 'كروفيت',
    emoji: 'crevette',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameFr: 'calamar',
    nameAr: 'كالامار',
    emoji: 'calamar',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  // Meats & Proteins

  {
    nameAr: 'تونة',
    nameFr: 'Thon',
    emoji: 'tuna',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 5,
    minThreshold: 2,
  },

  {
    nameAr: 'بيض',
    nameFr: 'Œufs',
    emoji: 'eggs',
    unit: 'unit' as MeasurementUnit,
    defaultQuantity: 60,
    minThreshold: 20,
  },
  // Vegetables
  {
    nameAr: 'طماطم',
    nameFr: 'Tomate',
    emoji: 'tomato',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameAr: 'بصل',
    nameFr: 'Oignon',
    emoji: 'onion',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 5,
    minThreshold: 2,
  },
  {
    nameAr: 'بصل أحمر',
    nameFr: 'Oignon rouge',
    emoji: 'red-onion',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 5,
    minThreshold: 2,
  },
  {
    nameAr: 'بطاطا',
    nameFr: 'Pomme de terre',
    emoji: 'potato',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 15,
    minThreshold: 5,
  },
  {
    nameAr: 'جزر',
    nameFr: 'Carotte',
    emoji: 'carrot',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 5,
    minThreshold: 2,
  },

  {
    nameAr: 'فلفل أحمر',
    nameFr: 'Poivron rouge',
    emoji: 'red-pepper',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 3,
    minThreshold: 1,
  },
  {
    nameFr: 'broccoli',
    nameAr: 'بروكلي',
    emoji: 'broccoli',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 3,
    minThreshold: 1,
  },

  {
    nameAr: 'خس',
    nameFr: 'Laitue',
    emoji: 'lettuce',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 3,
    minThreshold: 1,
  },
  {
    nameAr: 'برتقال',
    nameFr: 'Orange',
    emoji: 'orange',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameAr: 'موز',
    nameFr: 'Banane',
    emoji: 'banana',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameFr: 'kiwi',
    nameAr: 'كيوي',
    emoji: 'kiwi',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameFr: 'mangue',
    nameAr: 'مانجو',
    emoji: 'mango',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameFr: 'pêche',
    nameAr: 'خوخ',
    emoji: 'peach',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameFr: 'ananas',
    nameAr: 'أناناس',
    emoji: 'pineapple',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameFr: "huile d'olive", nameAr: 'زيت زيتون',
    emoji: 'olive-oil',
    unit: 'l' as MeasurementUnit,
    defaultQuantity: 5,
    minThreshold: 2,
  },
  {
    nameFr: 'ail', nameAr: 'ثوم',
    emoji: 'garlic',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 3,
    minThreshold: 1,
  },
  {
    nameFr: "huile de table", nameAr: 'زيت طهي',
    emoji: 'vegetable-oil',
    unit: 'l' as MeasurementUnit,
    defaultQuantity: 5,
    minThreshold: 2,
  },
  {
    nameAr: 'افوكادو',
    nameFr: 'Avocado',
    emoji: 'avocado',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameAr: 'تفاح',
    nameFr: 'Apple',
    emoji: 'apple',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  // Dairy
  {
    nameAr: 'حليب',
    nameFr: 'Lait',
    emoji: 'milk',
    unit: 'l' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameAr: 'قهوة',
    nameFr: 'Coffee',
    emoji: 'coffee',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 5,
  },
  {
    nameAr: 'شاي',
    nameFr: 'Tea',
    emoji: 'tea',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 5,
  },
  {
    nameAr: 'جبنة',
    nameFr: 'Jben',
    emoji: 'jben',
    unit: 'unit' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 5,
  },
  {
    nameAr: 'زبدة',
    nameFr: 'Beurre',
    emoji: 'butter',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 2,
    minThreshold: 1,
  },
  {
    nameAr: 'جبن',
    nameFr: 'Fromage',
    emoji: 'cheese',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 3,
    minThreshold: 1,
  },
  {
    nameAr: 'تورتيلا',
    nameFr: 'tortilla',
    emoji: 'tortilla',
    unit: 'unit' as MeasurementUnit,
    defaultQuantity: 100,
    minThreshold: 50,
  },
  {
    nameAr: 'زبادي',
    nameFr: 'Yaourt',
    emoji: 'yagourt',
    unit: 'unit' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 5,
  },
  {
    nameAr: 'فطر',
    nameFr: 'Champignon',
    emoji: 'mushroom',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 3,
    minThreshold: 1,
  },
  {
    nameAr: ' الجبن البرجر',
    nameFr: 'Fromage burger',
    emoji: 'cheese-slice',
    unit: 'unit' as MeasurementUnit,
    defaultQuantity: 100,
    minThreshold: 50,
  },
  {
    nameAr: 'بارميزانو',
    nameFr: 'Parmesan',
    emoji: 'cheese1',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 5,
    minThreshold: 1,
  },
  {
    nameFr: 'sauce tomate',
    nameAr: 'صلصة الطماطم',
    emoji: 'can',
    unit: 'l' as MeasurementUnit,
    defaultQuantity: 1,
    minThreshold: 1,
  },
  {
    nameFr: 'nutella',
    nameAr: 'نوتيلا',
    emoji: 'nutella',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 1,
    minThreshold: 1,
  },
  {
    nameAr: 'موزاريلا',
    nameFr: 'Mozzarella',
    emoji: 'mozzarella',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 3,
    minThreshold: 1,
  },

  // Grains & Pasta
  {
    nameAr: 'أرز',
    nameFr: 'Riz',
    emoji: 'rice',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 20,
    minThreshold: 5,
  },
  {
    nameAr: 'بطاطس مقلية',
    nameFr: 'Frites',
    emoji: 'french-fries',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameAr: 'معكرونة',
    nameFr: 'Pâtes',
    emoji: 'pasta',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameAr: 'لازانيا',
    nameFr: 'Lasagne',
    emoji: 'lasagne',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameAr: 'تغلياتيل',
    nameFr: 'Tagliatelle',
    emoji: 'tagliatelle',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },

  {
    nameAr: 'سباغيتي',
    nameFr: 'Spaghetti',
    emoji: 'spaghetti',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 3,
  },
  {
    nameAr: 'خبز البرجر',
    nameFr: 'Pain de burger',
    emoji: 'burger-bun',
    unit: 'unit' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 1,
  },
  {
    nameAr: 'خبز توست',
    nameFr: 'Pain de toast',
    emoji: 'toast',
    unit: 'unit' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 1,
  },
  {
    nameAr: 'خبز بانيني',
    nameFr: 'pain de Panini',
    emoji: 'panini',
    unit: 'unit' as MeasurementUnit,
    defaultQuantity: 10,
    minThreshold: 1,
  },
  {
    nameAr: 'دقيق',
    nameFr: 'Farine',
    emoji: 'flour',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 15,
    minThreshold: 5,
  },

  // Oils & Condiments
  {
    nameAr: 'زيت زيتون',
    nameFr: "Huile d'olive",
    emoji: 'olive-oil',
    unit: 'l' as MeasurementUnit,
    defaultQuantity: 5,
    minThreshold: 2,
  },

  // Sweet Spreads & Toppings

  // Dairy & Creams
  {
    nameFr: 'Crème fraîche',
    nameAr: 'كريمة فريش',
    emoji: 'cream',
    unit: 'l' as MeasurementUnit,
    defaultQuantity: 5,
    minThreshold: 2,
  },
  {
    nameAr: 'ماسكاربون',
    nameFr: 'Mascarpone',
    emoji: 'mascarpone',
    unit: 'kg' as MeasurementUnit,
    defaultQuantity: 2,
    minThreshold: 1,
  },
  {
    nameAr: 'كريمة مخفوقة',
    nameFr: 'Crème chantilly',
    emoji: 'whipped-cream',
    unit: 'l' as MeasurementUnit,
    defaultQuantity: 3,
    minThreshold: 1,
  },

  // Breads & Pastries
  {
    nameAr: 'باغيت',
    nameFr: 'Baguette',
    emoji: 'baguette',
    unit: 'unit' as MeasurementUnit,
    defaultQuantity: 20,
    minThreshold: 10,
  },
  {
    nameAr: 'كريب',
    nameFr: 'Crêpe',
    emoji: 'crepe',
    unit: 'unit' as MeasurementUnit,
    defaultQuantity: 50,
    minThreshold: 20,
  },
  {
    nameAr: 'بان كيك',
    nameFr: 'Pancake',
    emoji: 'pancake',
    unit: 'unit' as MeasurementUnit,
    defaultQuantity: 30,
    minThreshold: 15,
  },
  {
    nameAr: 'ورقة',
    nameFr: 'Warqa',
    emoji: 'warqa',
    unit: 'unit' as MeasurementUnit,
    defaultQuantity: 50,
    minThreshold: 20,
  },
  {
    nameAr: 'مسمن',
    nameFr: "M'semen",
    emoji: 'msemen',
    unit: 'unit' as MeasurementUnit,
    defaultQuantity: 30,
    minThreshold: 15,
  },


];

export function Inventory() {
  const { t, i18n } = useTranslation();
  const { selectedRestaurantId } = useRestaurantContext();
  const {
    data: ingredients,
    isLoading: ingredientsLoading,
    refetch: refetchIngredients,
  } = useIngredients(selectedRestaurantId);
  const {
    data: inventory,
    isLoading: inventoryLoading,
    refetch: refetchInventory,
  } = useInventory(selectedRestaurantId);

  // Get assistant auth context for permission checks
  const { data: assistant } = useCurrentAssistant();
  const isAssistant = !!assistant;

  // Permission helper function
  const hasPermission = (page: keyof AssistantPermissions, action: string): boolean => {
    if (!assistant?.permissions || !assistant.permissions[page]) return false;
    const pagePermissions = assistant.permissions[page] as Record<string, boolean>;
    return pagePermissions[action] === true;
  };

  // Calculate permissions - admin sees everything, assistants see based on permissions
  const canAddReceipt = !isAssistant || hasPermission('stock', 'add_receipt');
  const canValidateStock = !isAssistant || hasPermission('stock', 'add_validation');
  const canEditInventory = !isAssistant || hasPermission('stock', 'edit_receipt');
  const canDeleteInventory = !isAssistant || hasPermission('stock', 'delete_receipt');

  // Users with add_receipt permission can also add ingredients since adding ingredients creates receipts
  const canAddIngredients =
    !isAssistant || hasPermission('stock', 'add_receipt') || hasPermission('stock', 'edit_receipt');

  const [showCreateIngredientDialog, setShowCreateIngredientDialog] = useState(false);
  const [showQuickStartDialog, setShowQuickStartDialog] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState<Ingredient | null>(null);
  const [showAddReceiptDialog, setShowAddReceiptDialog] = useState(false);
  const [showMultiReceiptDialog, setShowMultiReceiptDialog] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<InventoryType | null>(null);
  const [alertDialog, setAlertDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
  }>({
    open: false,
    title: '',
    description: '',
  });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => { },
  });

  const handleEditIngredient = (ingredient: Ingredient, inventoryItem?: InventoryType) => {
    setEditingIngredient(ingredient);
    if (inventoryItem) {
      setSelectedInventoryItem(inventoryItem);
    } else {
      setSelectedInventoryItem(null);
    }
  };

  const handleAddReceipt = (inventoryItem: InventoryType) => {
    setSelectedInventoryItem(inventoryItem);
    setShowAddReceiptDialog(true);
  };

  const handleValidateStock = (inventoryItem: InventoryType) => {
    setSelectedInventoryItem(inventoryItem);
    setShowValidationDialog(true);
  };

  const getInventoryForIngredient = (ingredientId: string) => {
    return inventory?.find((inv) => inv.ingredientId === ingredientId);
  };

  const handleChartIngredientClick = useCallback((ingredientId: string) => {
    const card = document.getElementById(`ingredient-card-${ingredientId}`);
    if (card) {
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      card.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
      setTimeout(() => {
        card.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
      }, 2000);
    }
  }, []);

  if (!selectedRestaurantId) {
    return (
      <ProtectedRoute requiredRole="admin" requiredPermission="stock">
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
          <Header />
          <main className="container mx-auto px-4 py-8">
            <div className="p-4 text-center text-muted-foreground">
              {t('restaurant.selectRestaurant')}
            </div>
          </main>
        </div>
      </ProtectedRoute>
    );
  }

  if (ingredientsLoading || inventoryLoading) {
    return (
      <ProtectedRoute requiredRole="admin" requiredPermission="stock">
        <LoadingSpinner fullScreen />
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute requiredRole="admin" requiredPermission="stock">
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <main className="container mx-auto px-4 py-8">
          {/* Header + Quick Actions */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-3xl font-bold mb-1">{t('inventory.title')}</h1>
                <p className="text-muted-foreground text-sm">{t('inventory.description')}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {canAddIngredients && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      await refetchIngredients();
                      setShowQuickStartDialog(true);
                    }}
                  >
                    <Sparkles className="w-4 h-4 me-1.5" />
                    <span className="truncate">{t('inventory.quickStart.title')}</span>
                  </Button>
                )}
                {canAddIngredients && (
                  <Button
                    size="sm"
                    onClick={() => setShowCreateIngredientDialog(true)}
                  >
                    <Plus className="w-4 h-4 me-1.5" />
                    <span className="truncate">{t('ingredient.createIngredient')}</span>
                  </Button>
                )}
                {canAddReceipt && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMultiReceiptDialog(true)}
                    disabled={!ingredients || ingredients.length === 0}
                  >
                    <Receipt className="w-4 h-4 me-1.5" />
                    <span className="truncate">{t('inventory.addMultiReceipt')}</span>
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {/* Cost Analytics - Top */}
            <CostAnalyticsDashboard restaurantId={selectedRestaurantId} />

            {/* Tabs - Overview, Receipts, Validations */}
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="mb-6 flex-wrap h-auto gap-1.5 p-1.5 bg-muted/80">
                <TabsTrigger
                  value="overview"
                  className="gap-2 text-foreground bg-background/90 border border-input shadow-sm data-[state=inactive]:hover:bg-background data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-sm"
                >
                  <LayoutDashboard className="h-4 w-4 shrink-0" />
                  <span>{t('inventory.tabs.overview')}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="receipts"
                  className="gap-2 text-foreground bg-background/90 border border-input shadow-sm data-[state=inactive]:hover:bg-background data-[state=inactive]:hover:border-emerald-300 data-[state=inactive]:hover:text-emerald-700 dark:data-[state=inactive]:hover:border-emerald-600 dark:data-[state=inactive]:hover:text-emerald-300 data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:border-transparent dark:data-[state=active]:bg-emerald-500 data-[state=active]:shadow-sm"
                >
                  <Receipt className="h-4 w-4 shrink-0" />
                  <span>{t('inventory.tabs.receipts')}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="validations"
                  className="gap-2 text-foreground bg-background/90 border border-input shadow-sm data-[state=inactive]:hover:bg-background data-[state=inactive]:hover:border-amber-300 data-[state=inactive]:hover:text-amber-700 dark:data-[state=inactive]:hover:border-amber-500 dark:data-[state=inactive]:hover:text-amber-300 data-[state=active]:bg-amber-500 data-[state=active]:text-white data-[state=active]:border-transparent dark:data-[state=active]:bg-amber-400 dark:data-[state=active]:text-gray-900 data-[state=active]:shadow-sm"
                >
                  <ClipboardCheck className="h-4 w-4 shrink-0" />
                  <span>{t('inventory.tabs.validations')}</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6">
                <InventoryOverviewChart
                  restaurantId={selectedRestaurantId}
                  onIngredientClick={handleChartIngredientClick}
                />
                {!ingredients || ingredients.length === 0 ? (
                  <Card>
                    <CardContent className="py-8">
                      <p className="text-center text-muted-foreground">
                        {t('inventory.noIngredients')}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 items-stretch">
                    {ingredients.map((ingredient) => {
                      const inv = getInventoryForIngredient(ingredient.id);
                      const isOutOfStock = inv && inv.quantity <= 0;
                      const isLowStock =
                        inv && inv.quantity > 0 && inv.quantity <= inv.minThreshold;
                      return (
                        <Card key={ingredient.id} id={`ingredient-card-${ingredient.id}`} className="flex flex-col h-full transition-all duration-300">
                          <CardContent className="pt-4 px-4 pb-0 flex flex-col flex-1 min-h-0">
                            <div className="flex flex-col space-y-3 flex-1 min-h-0">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <IngredientIcon
                                    ingredient={ingredient}
                                    size={28}
                                    className="w-10 h-10 sm:w-10 sm:h-10 flex-shrink-0"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <p className="font-medium text-sm sm:text-base truncate">
                                      {i18n.language === 'ar'
                                        ? ingredient.nameAr
                                        : ingredient.nameFr}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                                      {inv ? (
                                        <>
                                          <Lock className="w-3 h-3" />
                                          <span>
                                            {inv.quantity.toFixed(3)}{' '}
                                            {t(`inventory.units.${inv.unit}`)}
                                          </span>
                                          {inv.averageCost > 0 && (
                                            <span className="text-xs">
                                              ({inv.averageCost.toFixed(2)} {t('common.currency')}
                                              /{inv.unit})
                                            </span>
                                          )}
                                        </>
                                      ) : (
                                        <span>{t('inventory.noStock')}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {inv &&
                                  (isOutOfStock ? (
                                    <span className="text-xs px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded flex items-center gap-1">
                                      <XCircle className="w-3 h-3" />
                                      {t('inventory.insights.outOfStock')}
                                    </span>
                                  ) : isLowStock ? (
                                    <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded">
                                      {t('inventory.lowStock')}
                                    </span>
                                  ) : (
                                    <span className="text-xs px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded">
                                      {t('inventory.inStock')}
                                    </span>
                                  ))}
                              </div>
                              {/* Edit and Delete buttons - top right below stock badge */}
                              {(canEditInventory || canDeleteInventory) && (
                                <div className="flex items-center justify-end gap-2">
                                  {canEditInventory && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleEditIngredient(ingredient, inv)}
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {canDeleteInventory && (
                                    <DeleteIngredientButton
                                      ingredient={ingredient}
                                      restaurantId={selectedRestaurantId}
                                      onSuccess={() => {
                                        refetchIngredients();
                                        refetchInventory();
                                      }}
                                      onShowAlert={(title, description) => {
                                        setAlertDialog({ open: true, title, description });
                                      }}
                                      onShowConfirm={(title, description, onConfirm) => {
                                        setConfirmDialog({
                                          open: true,
                                          title,
                                          description,
                                          onConfirm,
                                        });
                                      }}
                                    />
                                  )}
                                </div>
                              )}
                              {inv && inv.totalValue > 0 && (
                                <div className="text-sm">
                                  <span className="text-muted-foreground">
                                    {t('inventory.totalValueLabel')}:{' '}
                                  </span>
                                  <span className="font-bold text-primary">
                                    {inv.totalValue.toFixed(2)} {t('common.currency')}
                                  </span>
                                </div>
                              )}
                            </div>
                          </CardContent>
                          {/* Main action buttons - at the bottom with padding */}
                          <div className="px-4 pb-4 pt-4 space-y-2">
                            {inv ? (
                              <>
                                {canAddReceipt && (
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => handleAddReceipt(inv)}
                                    className="w-full flex items-center justify-center gap-1"
                                  >
                                    <ShoppingBag className="w-4 h-4 flex-shrink-0" />
                                    <span className="truncate">{t('inventory.addReceipt')}</span>
                                  </Button>
                                )}
                                {canValidateStock && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleValidateStock(inv)}
                                    className="w-full flex items-center justify-center gap-1"
                                  >
                                    <ClipboardCheck className="w-4 h-4 flex-shrink-0" />
                                    <span className="truncate">
                                      {t('inventory.validateStock')}
                                    </span>
                                  </Button>
                                )}
                              </>
                            ) : (
                              canAddIngredients && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditIngredient(ingredient)}
                                  className="w-full"
                                >
                                  {t('inventory.setupInventory')}
                                </Button>
                              )
                            )}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="receipts">
                {selectedRestaurantId && (
                  <ReceiptHistoryTable restaurantId={selectedRestaurantId} />
                )}
              </TabsContent>

              <TabsContent value="validations">
                {selectedRestaurantId && (
                  <ValidationHistoryTable restaurantId={selectedRestaurantId} />
                )}
              </TabsContent>
            </Tabs>

            {/* Analytics Section */}
            <div className="space-y-6">
              <InventoryEvolutionChart restaurantId={selectedRestaurantId} />
            </div>
          </div>
        </main>

        {/* Quick Start Dialog */}
        {showQuickStartDialog && selectedRestaurantId && (
          <QuickStartDialog
            restaurantId={selectedRestaurantId}
            existingIngredients={ingredients || []}
            open={showQuickStartDialog}
            onOpenChange={setShowQuickStartDialog}
            onSuccess={() => {
              refetchIngredients();
              refetchInventory();
            }}
            onShowAlert={(title, description) => {
              setAlertDialog({ open: true, title, description });
            }}
          />
        )}

        {/* Create Ingredient Dialog */}
        {showCreateIngredientDialog && selectedRestaurantId && (
          <IngredientDialog
            restaurantId={selectedRestaurantId}
            open={showCreateIngredientDialog}
            onOpenChange={setShowCreateIngredientDialog}
            onSuccess={() => refetchIngredients()}
          />
        )}

        {/* Edit Ingredient Dialog */}
        {editingIngredient && selectedRestaurantId && (
          <IngredientDialog
            restaurantId={selectedRestaurantId}
            ingredient={editingIngredient}
            inventoryItem={selectedInventoryItem}
            open={!!editingIngredient}
            onOpenChange={(open) => {
              if (!open) {
                setEditingIngredient(null);
                setSelectedInventoryItem(null);
              }
            }}
            onSuccess={() => {
              refetchIngredients();
              refetchInventory();
            }}
          />
        )}

        {/* Update Stock Dialog */}
        {/* Add Receipt Dialog */}
        {showAddReceiptDialog &&
          selectedInventoryItem &&
          selectedInventoryItem.ingredient &&
          selectedRestaurantId && (
            <AddReceiptDialog
              restaurantId={selectedRestaurantId}
              ingredient={selectedInventoryItem.ingredient}
              currentInventory={selectedInventoryItem}
              open={showAddReceiptDialog}
              onOpenChange={(open) => {
                setShowAddReceiptDialog(open);
                if (!open) setSelectedInventoryItem(null);
              }}
              onSuccess={() => refetchInventory()}
            />
          )}

        {/* Stock Validation Dialog */}
        {showValidationDialog &&
          selectedInventoryItem &&
          selectedInventoryItem.ingredient &&
          selectedRestaurantId && (
            <StockValidationDialog
              restaurantId={selectedRestaurantId}
              ingredient={selectedInventoryItem.ingredient}
              currentInventory={selectedInventoryItem}
              open={showValidationDialog}
              onOpenChange={(open) => {
                setShowValidationDialog(open);
                if (!open) setSelectedInventoryItem(null);
              }}
              onSuccess={() => refetchInventory()}
            />
          )}

        {/* Multi-Ingredient Receipt Dialog */}
        {showMultiReceiptDialog && selectedRestaurantId && ingredients && (
          <AddMultiIngredientReceiptDialog
            restaurantId={selectedRestaurantId}
            ingredients={ingredients}
            open={showMultiReceiptDialog}
            onOpenChange={(open) => {
              setShowMultiReceiptDialog(open);
            }}
            onSuccess={() => {
              refetchInventory();
            }}
          />
        )}

        <AlertModal
          open={alertDialog.open}
          onOpenChange={(open) => setAlertDialog({ ...alertDialog, open })}
          title={alertDialog.title}
          description={alertDialog.description}
        />

        <ConfirmModal
          open={confirmDialog.open}
          onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          description={confirmDialog.description}
          variant="destructive"
        />
      </div>
    </ProtectedRoute>
  );
}

// Quick Start Dialog Component
function QuickStartDialog({
  restaurantId,
  existingIngredients,
  open,
  onOpenChange,
  onSuccess,
  onShowAlert,
}: {
  restaurantId: string;
  existingIngredients: Ingredient[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  onShowAlert: (title: string, description: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customValues, setCustomValues] = useState<
    Map<
      number,
      {
        quantity: string;
        unit: MeasurementUnit;
        minThreshold: string;
        costPerUnit: string;
      }
    >
  >(new Map());

  const [supplier, setSupplier] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');

  const createIngredientMutation = useCreateIngredient();
  const addReceiptMutation = useAddReceipt(restaurantId);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setSupplier('');
      setInvoiceNumber('');
      setSelectedIndices(new Set());
      setCustomValues(new Map());
    }
  }, [open]);

  // Filter out ingredients that already exist
  const existingNames = new Set(
    existingIngredients.flatMap((ing) => [ing.nameAr.toLowerCase(), ing.nameFr.toLowerCase()])
  );

  const availableIngredients = TEMPLATE_INGREDIENTS.filter(
    (template) =>
      !existingNames.has(template.nameAr.toLowerCase()) &&
      !existingNames.has(template.nameFr.toLowerCase())
  );

  const toggleSelection = (index: number) => {
    const newSelection = new Set(selectedIndices);
    const newCustomValues = new Map(customValues);

    if (newSelection.has(index)) {
      newSelection.delete(index);
      newCustomValues.delete(index);
    } else {
      newSelection.add(index);
      // Initialize with default values
      const template = availableIngredients[index];
      newCustomValues.set(index, {
        quantity: String(template?.defaultQuantity || ''),
        unit: template?.unit || 'kg',
        minThreshold: String(template?.minThreshold || ''),
        costPerUnit: '', // Default cost - user should enter actual cost
      });
    }

    setSelectedIndices(newSelection);
    setCustomValues(newCustomValues);
  };

  const updateCustomValue = (
    index: number,
    field: 'quantity' | 'unit' | 'minThreshold' | 'costPerUnit',
    value: string | MeasurementUnit
  ) => {
    const newCustomValues = new Map(customValues);
    const current = newCustomValues.get(index) || {
      quantity: String(availableIngredients[index]?.defaultQuantity || ''),
      unit: availableIngredients[index]?.unit || 'kg',
      minThreshold: String(availableIngredients[index]?.minThreshold || ''),
      costPerUnit: '',
    };

    newCustomValues.set(index, {
      ...current,
      [field]: value,
    });

    setCustomValues(newCustomValues);
  };

  const handleSelectAll = () => {
    if (selectedIndices.size === availableIngredients.length) {
      setSelectedIndices(new Set());
      setCustomValues(new Map());
    } else {
      const allIndices = availableIngredients.map((_, idx) => idx);
      setSelectedIndices(new Set(allIndices));

      // Initialize custom values for all ingredients
      const newCustomValues = new Map<
        number,
        {
          quantity: string;
          unit: MeasurementUnit;
          minThreshold: string;
          costPerUnit: string;
        }
      >();

      allIndices.forEach((idx) => {
        const template = availableIngredients[idx];
        newCustomValues.set(idx, {
          quantity: String(template?.defaultQuantity || ''),
          unit: template?.unit || 'kg',
          minThreshold: String(template?.minThreshold || ''),
          costPerUnit: '',
        });
      });

      setCustomValues(newCustomValues);
    }
  };

  const handleSubmit = async () => {
    if (selectedIndices.size === 0) {
      onShowAlert(t('error.error'), t('inventory.selectAtLeastOneIngredient'));
      return;
    }

    // Validate that all selected ingredients have quantity and cost
    const invalidIngredients: string[] = [];
    for (const index of Array.from(selectedIndices)) {
      const template = availableIngredients[index];
      const customValue = customValues.get(index);
      const quantity = parseFloat(customValue?.quantity || '0');
      const costPerUnit = parseFloat(customValue?.costPerUnit || '0');

      if (isNaN(quantity) || quantity <= 0 || isNaN(costPerUnit) || costPerUnit <= 0) {
        const name = i18n.language === 'ar' ? template?.nameAr : template?.nameFr;
        invalidIngredients.push(name || `Item ${index + 1}`);
      }
    }

    if (invalidIngredients.length > 0) {
      onShowAlert(
        t('error.error'),
        t('inventory.quantityAndCostRequired', { items: invalidIngredients.join(', ') })
      );
      return;
    }

    setIsSubmitting(true);
    let successCount = 0;
    let failCount = 0;

    // Generate a batch ID only if adding multiple ingredients (for grouping)
    const batchId = selectedIndices.size > 1 ? crypto.randomUUID() : undefined;

    try {
      for (const index of Array.from(selectedIndices)) {
        const template = availableIngredients[index];
        const customValue = customValues.get(index);

        try {
          // Create ingredient - returns the ingredient ID
          const newIngredientId = await createIngredientMutation.mutateAsync({
            restaurantId,
            nameAr: template?.nameAr || '',
            nameFr: template?.nameFr || '',
            emoji: template?.emoji || 'dish',
          });

          // Create initial receipt (required - already validated above)
          const quantity = parseFloat(customValue?.quantity || '0');
          const costPerUnit = parseFloat(customValue?.costPerUnit || '0');

          // Pass min threshold directly to addReceipt (it will be set when creating inventory)
          const minThresholdStr = customValue?.minThreshold || String(template?.minThreshold || '0');
          const minThreshold = parseFloat(minThresholdStr);

          // Determine final supplier value: use input or default translated text
          const finalSupplier =
            supplier.trim() || (i18n.language === 'ar' ? 'بداية سريعة' : 'Démarrage Rapide');

          await addReceiptMutation.mutateAsync({
            ingredientId: newIngredientId,
            quantityReceived: quantity,
            unit: customValue?.unit || template?.unit || 'kg',
            costPerUnit: costPerUnit,
            receiptDate: new Date(),
            supplier: finalSupplier,
            invoiceNumber: invoiceNumber.trim() || undefined,
            notes: t('inventory.initialStockReceipt'),
            minThreshold: !isNaN(minThreshold) && minThreshold > 0 ? minThreshold : undefined,
            batchId: batchId, // Group receipts added together
          });

          successCount++;
        } catch (error) {
          console.error(`Failed to add ${template?.nameFr}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        onSuccess?.();
        onOpenChange(false);
        onShowAlert(
          t('common.success'),
          t('inventory.ingredientsAdded', { count: successCount, total: selectedIndices.size })
        );
      }

      if (failCount > 0) {
        onShowAlert(t('error.error'), t('inventory.someIngredientsFailed', { count: failCount }));
      }
    } catch (error) {
      onShowAlert(t('error.error'), t('error.generic'));
    } finally {
      setIsSubmitting(false);
      setSelectedIndices(new Set());
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:w-[90vw] sm:max-w-[90vw] md:min-w-[60vw] md:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            {t('inventory.quickStart.title')}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {t('inventory.quickStartDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {availableIngredients.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>{t('inventory.allEssentialIngredientsAdded')}</p>
            </div>
          ) : (
            <>
              {/* Receipt Information Section */}
              <div className="space-y-3 pb-4 border-b">
                <div>
                  <h3 className="text-sm font-semibold">{t('inventory.quickStart.receiptInfo')}</h3>
                  <p className="text-xs text-muted-foreground">
                    {t('inventory.quickStart.receiptInfoDescription')}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="quickstart-supplier" className="text-xs">
                      {t('inventory.addReceiptDialog.supplier')}
                    </Label>
                    <Input
                      id="quickstart-supplier"
                      value={supplier}
                      onChange={(e) => setSupplier(e.target.value)}
                      placeholder={t('inventory.quickStart.supplierPlaceholder')}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="quickstart-invoice" className="text-xs">
                      {t('inventory.addReceiptDialog.invoiceNumber')}
                    </Label>
                    <Input
                      id="quickstart-invoice"
                      value={invoiceNumber}
                      onChange={(e) => setInvoiceNumber(e.target.value)}
                      placeholder={t('inventory.quickStart.invoicePlaceholder')}
                      className="h-9 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Selection controls */}
              <div className="flex items-center justify-between pb-2 border-b">
                <span className="text-sm font-medium">
                  {t('inventory.selectedCount', { count: selectedIndices.size })}
                </span>
                <Button type="button" variant="ghost" size="sm" onClick={handleSelectAll}>
                  {selectedIndices.size === availableIngredients.length
                    ? t('common.deselectAll')
                    : t('common.selectAll')}
                </Button>
              </div>

              {/* Ingredient cards grid */}
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {availableIngredients.map((template, index) => {
                  const isSelected = selectedIndices.has(index);
                  const customValue = customValues.get(index);

                  return (
                    <Card
                      key={index}
                      className={`transition-all ${isSelected ? 'ring-2 ring-primary bg-primary/5' : 'hover:border-primary/50'
                        }`}
                    >
                      <CardContent className="p-3 space-y-3">
                        {/* Header - clickable to toggle selection */}
                        <div
                          className="flex items-center gap-3 cursor-pointer"
                          onClick={() => toggleSelection(index)}
                        >
                          <div className="flex-shrink-0">
                            <IngredientIcon
                              ingredient={
                                {
                                  nameAr: template.nameAr,
                                  nameFr: template.nameFr,
                                  emoji: template.emoji,
                                } as Ingredient
                              }
                              size={32}
                              className="w-8 h-8"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {i18n.language === 'ar' ? template.nameAr : template.nameFr}
                            </p>
                            {!isSelected && (
                              <p className="text-xs text-muted-foreground">
                                {template.defaultQuantity} {t(`inventory.units.${template.unit}`)}
                              </p>
                            )}
                          </div>
                          <div className="flex-shrink-0">
                            <div
                              className={`w-5 h-5 rounded-full flex items-center justify-center ${isSelected ? 'bg-primary' : 'border-2 border-muted-foreground/30'
                                }`}
                            >
                              {isSelected && (
                                <svg
                                  className="w-3 h-3 text-primary-foreground"
                                  fill="none"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path d="M5 13l4 4L19 7"></path>
                                </svg>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Editable fields - only shown when selected */}
                        {isSelected && (
                          <div
                            className="space-y-2 pt-2 border-t"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label htmlFor={`quantity-${index}`} className="text-xs">
                                  {t('inventory.quantity')} *
                                </Label>
                                <Input
                                  id={`quantity-${index}`}
                                  type="number"
                                  step="0.01"
                                  min="0.01"
                                  value={customValue?.quantity ?? String(template.defaultQuantity)}
                                  onChange={(e) =>
                                    updateCustomValue(
                                      index,
                                      'quantity',
                                      e.target.value
                                    )
                                  }
                                  className={`h-8 text-xs ${parseFloat(customValue?.quantity || '0') <= 0 ? 'border-amber-500' : ''}`}
                                  required
                                />
                              </div>
                              <div className="space-y-1">
                                <Label htmlFor={`unit-${index}`} className="text-xs">
                                  {t('inventory.unit')}
                                </Label>
                                <Select
                                  value={customValue?.unit || template.unit}
                                  onValueChange={(value) =>
                                    updateCustomValue(index, 'unit', value as MeasurementUnit)
                                  }
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="g">{t('inventory.units.g')}</SelectItem>
                                    <SelectItem value="kg">{t('inventory.units.kg')}</SelectItem>
                                    <SelectItem value="ml">{t('inventory.units.ml')}</SelectItem>
                                    <SelectItem value="cl">{t('inventory.units.cl')}</SelectItem>
                                    <SelectItem value="l">{t('inventory.units.l')}</SelectItem>
                                    <SelectItem value="unit">
                                      {t('inventory.units.unit')}
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label htmlFor={`costPerUnit-${index}`} className="text-xs">
                                  {t('inventory.costPerUnit')} ({t('common.currency')}) *
                                </Label>
                                <Input
                                  id={`costPerUnit-${index}`}
                                  type="number"
                                  step="0.01"
                                  min="0.01"
                                  value={customValue?.costPerUnit ?? ''}
                                  onChange={(e) =>
                                    updateCustomValue(
                                      index,
                                      'costPerUnit',
                                      e.target.value
                                    )
                                  }
                                  className={`h-8 text-xs ${parseFloat(customValue?.costPerUnit || '0') <= 0 ? 'border-amber-500' : ''}`}
                                  placeholder="0.00"
                                  required
                                />
                              </div>
                              <div className="space-y-1">
                                <Label htmlFor={`minThreshold-${index}`} className="text-xs">
                                  {t('inventory.minThreshold')}
                                </Label>
                                <Input
                                  id={`minThreshold-${index}`}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={customValue?.minThreshold ?? String(template.minThreshold)}
                                  onChange={(e) =>
                                    updateCustomValue(
                                      index,
                                      'minThreshold',
                                      e.target.value
                                    )
                                  }
                                  className="h-8 text-xs"
                                />
                              </div>
                            </div>
                            {/* Show total cost preview */}
                            {parseFloat(customValue?.quantity || '0') > 0 &&
                              parseFloat(customValue?.costPerUnit || '0') > 0 && (
                                <div className="text-xs text-muted-foreground bg-muted/50 rounded p-1.5">
                                  {t('inventory.totalCostPreview')}:{' '}
                                  {(
                                    parseFloat(customValue?.quantity || '0') * parseFloat(customValue?.costPerUnit || '0')
                                  ).toFixed(2)}{' '}
                                  {t('common.currency')}
                                </div>
                              )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <p className="text-xs text-muted-foreground text-center pt-2">
                {t('inventory.clickToSelect')}
              </p>
            </>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            {t('common.cancel')}
          </Button>
          {availableIngredients.length > 0 && (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || selectedIndices.size === 0}
              className="w-full sm:w-auto"
            >
              {isSubmitting ? t('common.loading') : t('inventory.addSelectedIngredients')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Ingredient Dialog Component
function IngredientDialog({
  restaurantId,
  ingredient,
  inventoryItem,
  open,
  onOpenChange,
  onSuccess,
}: {
  restaurantId: string;
  ingredient?: Ingredient;
  inventoryItem?: InventoryType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const isEditing = !!ingredient;
  const [formData, setFormData] = useState({
    nameAr: ingredient?.nameAr || '',
    nameFr: ingredient?.nameFr || '',
    emoji: ingredient?.emoji || 'dish',
  });
  // Receipt fields for new ingredients (using strings to allow typing decimals like "0.4")
  const [receiptData, setReceiptData] = useState({
    quantity: '',
    unit: 'kg' as MeasurementUnit,
    costPerUnit: '',
    minThreshold: '',
  });
  const [supplier, setSupplier] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  // Min threshold for editing (from inventory)
  const [minThreshold, setMinThreshold] = useState(inventoryItem?.minThreshold || 0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useCreateIngredient();
  const updateMutation = useUpdateIngredient(ingredient?.id || '', restaurantId);
  const updateMinThresholdMutation = useUpdateMinThreshold(restaurantId);
  const addReceiptMutation = useAddReceipt(restaurantId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};
    // Require at least one language field
    if (!formData['nameAr'] && !formData['nameFr']) {
      newErrors['nameAr'] = t('validation.required');
      newErrors['nameFr'] = t('validation.required');
    }

    // For new ingredients, require quantity and cost
    if (!isEditing) {
      const qty = parseFloat(receiptData.quantity);
      const cost = parseFloat(receiptData.costPerUnit);
      if (!receiptData.quantity || isNaN(qty) || qty <= 0) {
        newErrors['quantity'] = t('validation.required');
      }
      if (!receiptData.costPerUnit || isNaN(cost) || cost <= 0) {
        newErrors['costPerUnit'] = t('validation.required');
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      if (isEditing) {
        // Update ingredient info
        await updateMutation.mutateAsync(formData);
        // Update min threshold if inventory exists
        if (inventoryItem && minThreshold !== inventoryItem.minThreshold) {
          await updateMinThresholdMutation.mutateAsync({
            inventoryId: inventoryItem.id,
            minThreshold: minThreshold,
          });
        }
      } else {
        // Create ingredient and add initial receipt (required)
        const newIngredientId = await createMutation.mutateAsync({ ...formData, restaurantId });

        // Determine final supplier value: use input or default translated text
        const finalSupplier =
          supplier.trim() || (t('common.language') === 'ar' ? 'إضافة مكون' : 'Ajout Ingrédient');

        // Pass min threshold directly to addReceipt (it will be set when creating inventory)
        const parsedMinThreshold = parseFloat(receiptData.minThreshold);
        await addReceiptMutation.mutateAsync({
          ingredientId: newIngredientId,
          quantityReceived: parseFloat(receiptData.quantity),
          unit: receiptData.unit,
          costPerUnit: parseFloat(receiptData.costPerUnit),
          receiptDate: new Date(),
          supplier: finalSupplier,
          invoiceNumber: invoiceNumber.trim() || undefined,
          notes: t('inventory.initialStockReceipt'),
          minThreshold: !isNaN(parsedMinThreshold) && parsedMinThreshold > 0 ? parsedMinThreshold : undefined,
        });
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      setErrors({ form: error instanceof Error ? error.message : t('error.generic') });
    }
  };

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    updateMinThresholdMutation.isPending ||
    addReceiptMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:w-[90vw] sm:max-w-[90vw] md:min-w-[50vw] md:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">
              {isEditing ? t('ingredient.editIngredient') : t('ingredient.createIngredient')}
            </DialogTitle>
            <DialogDescription className="text-sm">
              {t('inventory.ingredientFormDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Icon Picker */}
            <IngredientIconPicker
              value={formData['emoji'] || 'dish'}
              onSelect={(iconId) => setFormData({ ...formData, emoji: iconId })}
              label={t('ingredient.emoji')}
            />

            <div className="space-y-2">
              <Label htmlFor="nameAr" className="text-sm sm:text-base">
                {t('ingredient.name')} (AR)
              </Label>
              <Input
                id="nameAr"
                value={formData['nameAr']}
                onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                disabled={isPending}
                className="text-sm sm:text-base"
              />
              {errors['nameAr'] && (
                <p className="text-xs sm:text-sm text-destructive">{errors['nameAr']}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="nameFr" className="text-sm sm:text-base">
                {t('ingredient.name')} (FR)
              </Label>
              <Input
                id="nameFr"
                value={formData['nameFr']}
                onChange={(e) => setFormData({ ...formData, nameFr: e.target.value })}
                disabled={isPending}
                className="text-sm sm:text-base"
              />
              {errors['nameFr'] && (
                <p className="text-xs sm:text-sm text-destructive">{errors['nameFr']}</p>
              )}
            </div>

            {/* Min Threshold - Only for editing with existing inventory */}
            {isEditing && inventoryItem && (
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-sm mb-3">{t('inventory.stockSettings')}</h4>
                <div className="space-y-1">
                  <Label htmlFor="minThreshold" className="text-xs">
                    {t('inventory.minThreshold')}
                  </Label>
                  <Input
                    id="minThreshold"
                    type="number"
                    step="0.01"
                    min="0"
                    value={minThreshold || ''}
                    onChange={(e) => setMinThreshold(parseFloat(e.target.value) || 0)}
                    disabled={isPending}
                    className="h-9 text-sm"
                    placeholder="0"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('inventory.minThresholdDescription')}
                  </p>
                </div>
              </div>
            )}

            {/* Initial Stock Section - Only for new ingredients */}
            {!isEditing && (
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-sm mb-3">{t('inventory.initialStock')} *</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="quantity" className="text-xs">
                      {t('inventory.quantity')} *
                    </Label>
                    <Input
                      id="quantity"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={receiptData.quantity}
                      onChange={(e) =>
                        setReceiptData({
                          ...receiptData,
                          quantity: e.target.value,
                        })
                      }
                      disabled={isPending}
                      className={`h-9 text-sm ${errors['quantity'] ? 'border-destructive' : ''}`}
                      placeholder="0"
                      required
                    />
                    {errors['quantity'] && (
                      <p className="text-xs text-destructive">{errors['quantity']}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="unit" className="text-xs">
                      {t('inventory.unit')}
                    </Label>
                    <Select
                      value={receiptData.unit}
                      onValueChange={(value) =>
                        setReceiptData({ ...receiptData, unit: value as MeasurementUnit })
                      }
                      disabled={isPending}
                    >
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="g">{t('inventory.units.g')}</SelectItem>
                        <SelectItem value="kg">{t('inventory.units.kg')}</SelectItem>
                        <SelectItem value="ml">{t('inventory.units.ml')}</SelectItem>
                        <SelectItem value="cl">{t('inventory.units.cl')}</SelectItem>
                        <SelectItem value="l">{t('inventory.units.l')}</SelectItem>
                        <SelectItem value="unit">{t('inventory.units.unit')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="costPerUnit" className="text-xs">
                      {t('inventory.costPerUnit')} ({t('common.currency')}) *
                    </Label>
                    <Input
                      id="costPerUnit"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={receiptData.costPerUnit}
                      onChange={(e) =>
                        setReceiptData({
                          ...receiptData,
                          costPerUnit: e.target.value,
                        })
                      }
                      disabled={isPending}
                      className={`h-9 text-sm ${errors['costPerUnit'] ? 'border-destructive' : ''}`}
                      placeholder="0.00"
                      required
                    />
                    {errors['costPerUnit'] && (
                      <p className="text-xs text-destructive">{errors['costPerUnit']}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="minThreshold" className="text-xs">
                      {t('inventory.minThreshold')}
                    </Label>
                    <Input
                      id="minThreshold"
                      type="number"
                      step="0.01"
                      min="0"
                      value={receiptData.minThreshold}
                      onChange={(e) =>
                        setReceiptData({
                          ...receiptData,
                          minThreshold: e.target.value,
                        })
                      }
                      disabled={isPending}
                      className="h-9 text-sm"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Receipt Information */}
                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-medium text-xs mb-3">
                    {t('inventory.addIngredient.receiptFields')}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="ingredient-supplier" className="text-xs">
                        {t('inventory.addReceiptDialog.supplier')}
                      </Label>
                      <Input
                        id="ingredient-supplier"
                        value={supplier}
                        onChange={(e) => setSupplier(e.target.value)}
                        disabled={isPending}
                        placeholder={t('inventory.addIngredient.supplierPlaceholder')}
                        className="h-9 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="ingredient-invoice" className="text-xs">
                        {t('inventory.addReceiptDialog.invoiceNumber')}
                      </Label>
                      <Input
                        id="ingredient-invoice"
                        value={invoiceNumber}
                        onChange={(e) => setInvoiceNumber(e.target.value)}
                        disabled={isPending}
                        placeholder={t('inventory.addIngredient.invoicePlaceholder')}
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Total cost preview */}
                {parseFloat(receiptData.quantity) > 0 && parseFloat(receiptData.costPerUnit) > 0 && (
                  <div className="mt-3 text-sm text-muted-foreground bg-muted/50 rounded p-2">
                    {t('inventory.totalCostPreview')}:{' '}
                    {(parseFloat(receiptData.quantity) * parseFloat(receiptData.costPerUnit)).toFixed(2)}{' '}
                    {t('common.currency')}
                  </div>
                )}
              </div>
            )}

            {errors['form'] && (
              <div className="p-2 sm:p-3 text-xs sm:text-sm text-destructive bg-destructive/10 rounded-md">
                {errors['form']}
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
              className="w-full sm:w-auto text-sm sm:text-base"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              className="w-full sm:w-auto text-sm sm:text-base"
            >
              {isPending ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Update Stock Dialog Component
// Delete Ingredient Button Component
function DeleteIngredientButton({
  ingredient,
  restaurantId,
  onSuccess,
  onShowAlert,
  onShowConfirm,
}: {
  ingredient: Ingredient;
  restaurantId: string;
  onSuccess?: () => void;
  onShowAlert: (title: string, description: string) => void;
  onShowConfirm: (title: string, description: string, onConfirm: () => void) => void;
}) {
  const { t } = useTranslation();
  const deleteMutation = useDeleteIngredient(restaurantId);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    onShowConfirm(
      t('ingredient.deleteIngredient'),
      t('ingredient.deleteIngredient') + '?',
      async () => {
        setIsDeleting(true);
        try {
          await deleteMutation.mutateAsync(ingredient.id);
          onSuccess?.();
        } catch (error) {
          console.error('Failed to delete ingredient:', error);
          // Check if it's the INGREDIENT_IN_USE error
          if (error instanceof Error && error.message === 'INGREDIENT_IN_USE') {
            onShowAlert(
              t('ingredient.ingredientInUseTitle'),
              t('ingredient.ingredientInUseDescription')
            );
          } else {
            onShowAlert(t('error.error'), t('error.generic'));
          }
        } finally {
          setIsDeleting(false);
        }
      }
    );
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleDelete}
      disabled={isDeleting || deleteMutation.isPending}
    >
      <Trash2 className="w-4 h-4" />
    </Button>
  );
}
